import { JudgeType, MatchMode } from "@/types/conditionJudgeNodeType";
import { ReplyMode } from "@/types/endReplyNodeType";
import { sanitizeEndReplyVariableContent } from "@/components/agent-node/end-reply-variable-content";
import { extractInfoNodesData } from "../utils";
import { formatContentWithLabels } from "./formatContentWithLabels";
import { trim } from "lodash-es";

/**
 * 更新所有条件判断节点的分支内容显示
 * 当信息节点、系统变量、自定义变量发生变化时调用
 * @param graph - G6 图实例
 * @param systemVarsPool - 系统变量池
 * @param variablePool - 自定义变量池
 * @param taskType - 任务流类型：'agent'(智能任务流) 或 其他(标准任务流)
 */
export const updateAllConditionNodesBranchContent = (
  graph: any,
  systemVarsPool: any[],
  variablePool: any[],
  taskType: string = "",
) => {
  if (!graph) return;

  // 获取所有条件判断节点
  const conditionNodes = graph.findAll("node", (node) => {
    const model = node.getModel();
    return model.nodeData?.nodeType === "conditionJudgeNode";
  });

  // 获取所有信息节点数据
  const allInfoNodes = extractInfoNodesData(graph.getNodes());

  // 收集接口调用节点的输出参数（returns）
  const apiCallNodesData: Array<{
    nodeName: string;
    returns: Array<{ variable_id: string; key: string }>;
  }> = [];
  const nodes = graph.getNodes();
  nodes.forEach((node) => {
    const nodeModel = node.getModel ? node.getModel() : node;
    const nodeData = nodeModel.nodeData;
    // 收集所有接口调用节点的输出参数
    if (
      nodeData.nodeType === "interfaceInvocationNode" &&
      nodeData.returns &&
      Array.isArray(nodeData.returns)
    ) {
      apiCallNodesData.push({
        nodeName: nodeData.nodeName || "",
        returns: nodeData.returns.filter(
          (r) =>
            r.variable_id && r.key && r.variable_id.startsWith("userdefine-"),
        ),
      });
    }
  });

  // 将接口调用节点的输出参数转换为自定义变量格式
  const apiCallCustomVars = apiCallNodesData.flatMap((node) =>
    node.returns.map((ret) => ({
      value: ret.variable_id,
      label: ret.key,
      type: JudgeType.CUSTOM,
    })),
  );

  // 收集启动节点变量
  const inputVarsList: Array<{ value: string; label: string }> = [];
  nodes.forEach((node) => {
    const nodeModel = node.getModel ? node.getModel() : node;
    const nodeData = nodeModel.nodeData;
    if (
      nodeData.nodeType === "userIntentNode" &&
      nodeData.inputVariables &&
      Array.isArray(nodeData.inputVariables)
    ) {
      nodeData.inputVariables
        .filter((v) => v.name && v.name.trim())
        .forEach((v) => {
          inputVarsList.push({
            value: v.name,
            label: v.name,
          });
        });
    }
  });

  // 遍历所有条件判断节点
  conditionNodes.forEach((node) => {
    const nodeModel = node.getModel();
    let hasChanges = false;

    if (nodeModel.nodeData.branches) {
      const matchMode = nodeModel.nodeData.matchMode;
      nodeModel.nodeData.branches.forEach((branch) => {
        if (
          branch.conditions &&
          branch.conditions[0]?.content &&
          matchMode === MatchMode.AI
        ) {
          // 格式化分支内容
          const { display, content } = formatContentWithLabels(
            branch.conditions[0].content,
            {
              infoNodes: allInfoNodes,
              systemVars: systemVarsPool,
              customVars: [
                ...variablePool.map((v) => ({
                  value: v,
                  label: v,
                  type: JudgeType.CUSTOM,
                })),
                ...apiCallCustomVars,
              ],
              inputVars: inputVarsList,
            },
          );

          if (branch.content !== display) {
            // 暂时显示40个字符
            const sliceDisplay =
              display.length > 40 ? display.slice(0, 40) + "..." : display;
            // 兼容显示分支旧数据
            branch.content = sliceDisplay;
            branch.originContent = display;
            branch.conditions[0].content = content;
            hasChanges = true;

            // 更新相关的边的标签
            const outEdges = node.getOutEdges();
            outEdges.forEach((edge) => {
              const edgeModel = edge.getModel();
              if (edgeModel.edgeData?.branchId === branch.id) {
                edge.update({
                  // 附加上三角
                  label: sliceDisplay + " ▼",
                });
              }
            });
          }
        }
        if (matchMode === MatchMode.RULE) {
          // 手动添加规则模式下，info_id 存储格式为纯ID/纯值：
          // - 信息收集节点：info.id（纯ID）
          // - 接口调用节点：ret.variable_id（纯ID）
          // - 系统变量：v.value（纯值）
          const allInfoIds = [
            ...allInfoNodes.flatMap(
              (node) => node.infos?.map((info) => info.id) || [],
            ),
            ...apiCallNodesData.flatMap((node) =>
              node.returns.map((ret) => ret.variable_id),
            ),
            ...systemVarsPool.map((v) => v.value),
            ...variablePool,
            ...inputVarsList.map((v) => v.value),
          ].flat();

          // 在手动模式下，如果信息节点不存在，则删除
          branch.conditions.forEach((condition) => {
            const { info_id } = condition;

            if (!info_id) return;

            // 智能任务流使用特殊的格式处理：${1-info-xxx}
            // 标准任务流使用纯 ID 格式
            let actualInfoId = info_id;

            // 只在智能任务流中应用格式转换
            if (taskType?.includes("agent")) {
              // 提取 ${...} 格式中的实际 ID
              // 如果 info_id 是 ${1-info-xxx} 或 ${3-userdefine-xxx}，提取其中的 ID 部分
              if (info_id.includes("${") && info_id.includes("}")) {
                const match = info_id.match(/\$\{[0-9]+-([^}]+)\}/);
                if (match && match[1]) {
                  actualInfoId = match[1];
                }
              }
            }

            // 检查 actualInfoId 是否在有效 ID 列表中
            if (!allInfoIds.includes(actualInfoId)) {
              condition.info_id = "";
            }
          });
        }
      });
    }

    // 如果有变更，更新节点
    if (hasChanges) {
      node.update({
        nodeData: nodeModel.nodeData,
      });
    }
  });
};

export const updateAllInterfaceCallNodeParams = (graph: any) => {
  if (!graph?.getNodes) return;

  const nodes = graph.getNodes();
  const validInfoIds = new Set<string>();
  const validVariableIds = new Set<string>();
  const validInputVariableNames = new Set<string>();

  extractInfoNodesData(nodes).forEach((node) => {
    node.infos.forEach((info) => {
      if (info.id && info.id.startsWith("info-")) {
        validInfoIds.add(info.id);
      }
    });
  });

  nodes.forEach((node) => {
    const nodeModel = node.getModel ? node.getModel() : node;
    const nodeData = nodeModel.nodeData;

    if (
      nodeData.nodeType === "interfaceInvocationNode" &&
      Array.isArray(nodeData.returns)
    ) {
      nodeData.returns.forEach((ret) => {
        if (ret.variable_id && ret.variable_id.startsWith("userdefine-")) {
          validVariableIds.add(ret.variable_id);
        }
      });
    }

    if (
      nodeData.nodeType === "userIntentNode" &&
      Array.isArray(nodeData.inputVariables)
    ) {
      nodeData.inputVariables.forEach((item) => {
        const name = trim(item?.name || "");
        if (name) {
          validInputVariableNames.add(name);
        }
      });
    }
  });

  nodes.forEach((node) => {
    const nodeModel = node.getModel ? node.getModel() : node;
    const nodeData = nodeModel.nodeData;

    if (
      nodeData.nodeType !== "interfaceInvocationNode" ||
      !Array.isArray(nodeData.params)
    ) {
      return;
    }

    let hasChanges = false;
    const nextParams = nodeData.params.map((param) => {
      if (
        param.type === 1 &&
        param.value &&
        param.value.startsWith("info-") &&
        !validInfoIds.has(param.value)
      ) {
        hasChanges = true;
        return { ...param, key: "", value: "" };
      }

      if (
        param.type === 3 &&
        param.value &&
        param.value.startsWith("userdefine-") &&
        !validVariableIds.has(param.value)
      ) {
        hasChanges = true;
        return { ...param, key: "", value: "" };
      }

      if (param.type === 5) {
        const variableName = trim(param.value ?? "") || trim(param.key ?? "");
        if (variableName && !validInputVariableNames.has(variableName)) {
          hasChanges = true;
          return { ...param, key: "", value: "" };
        }
      }

      return param;
    });

    if (hasChanges) {
      node.update({
        nodeData: {
          ...nodeData,
          params: nextParams,
        },
      });
    }
  });
};

export const updateAllEndReplyNodeContent = (
  graph: any,
  systemVarsPool: Array<{ value: string; label: string }>,
) => {
  if (!graph?.getNodes) return;

  const nodes = graph.getNodes();
  const variables = [
    {
      value: JudgeType.SYSTEM,
      label: "system",
      type: JudgeType.SYSTEM,
      children: systemVarsPool.map((v) => ({
        value: `\${${JudgeType.SYSTEM}-${v.value}}`,
        label: v.label,
        type: JudgeType.SYSTEM,
      })),
    },
    ...extractInfoNodesData(nodes)
      .map((node) => ({
        value: node.nodeId,
        label: node.nodeName,
        type: JudgeType.INFO,
        children: node.infos
          .filter((info) => info.name && info.name.trim())
          .map((info) => ({
            value: `\${${JudgeType.INFO}-${info.id}}`,
            label: info.name,
            type: JudgeType.INFO,
          })),
      }))
      .filter((node) => (node.children?.length ?? 0) > 0),
    ...nodes
      .map((node) => {
        const nodeModel = node.getModel ? node.getModel() : node;
        const nodeData = nodeModel.nodeData;
        if (
          nodeData.nodeType !== "interfaceInvocationNode" ||
          !Array.isArray(nodeData.returns)
        ) {
          return null;
        }

        return {
          value: nodeModel.id || nodeData.id,
          label: nodeData.nodeName || "",
          type: JudgeType.CUSTOM,
          children: nodeData.returns
            .filter(
              (ret) =>
                ret.variable_id &&
                ret.key &&
                ret.variable_id.startsWith("userdefine-"),
            )
            .map((ret) => ({
              value: `\${${JudgeType.CUSTOM}-${ret.variable_id}}`,
              label: ret.key,
              type: JudgeType.CUSTOM,
            })),
        };
      })
      .filter((node) => node && (node.children?.length ?? 0) > 0),
    ...(() => {
      const inputChildren = nodes
        .flatMap((node) => {
          const nodeModel = node.getModel ? node.getModel() : node;
          const nodeData = nodeModel.nodeData;
          if (
            nodeData.nodeType !== "userIntentNode" ||
            !Array.isArray(nodeData.inputVariables)
          ) {
            return [];
          }

          return nodeData.inputVariables
            .filter((v) => v.name && v.name.trim())
            .map((v) => ({
              value: `\${${JudgeType.INPUT}-${v.name}}`,
              label: v.name,
              type: JudgeType.INPUT,
            }));
        })
        .filter(Boolean);

      return inputChildren.length > 0
        ? [
            {
              value: JudgeType.INPUT,
              label: "input",
              type: JudgeType.INPUT,
              children: inputChildren,
            },
          ]
        : [];
    })(),
  ];

  nodes.forEach((node) => {
    const nodeModel = node.getModel ? node.getModel() : node;
    const nodeData = nodeModel.nodeData;

    if (
      nodeData.nodeType !== "endReplyNode" ||
      nodeData.mode !== ReplyMode.FIXED ||
      !nodeData.content
    ) {
      return;
    }

    const { cleanedContent } = sanitizeEndReplyVariableContent(
      nodeData.content,
      variables as any,
    );

    if (cleanedContent !== nodeData.content) {
      node.update({
        nodeData: {
          ...nodeData,
          content: cleanedContent,
        },
      });
    }
  });
};
